import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { CONNECTED_CALL } from "../../helpers/url_helper";
import CommanDataTable from "../../Components/Common/DataTable";
import moment from "moment";
import { formatDate } from "../../Components/Common/FormDateTime";
import { APIClient } from "../../helpers/api_helper";
import { useProfileData } from "../../Components/Hooks/UserHooks";
import { HandleErrorMessage } from "../../Components/Common/HandleErrorMessage";
import { formatTime } from "../../Components/Common/FlatePicker";

const api = new APIClient();

const ConnectedConsolidate = ({
  appliedFilters,
  selectedFromDate,
  selectedEmployeeOptions,
  selectedToDate,
  selectedEmployeeOptions2
}) => {
  document.title = "Connected Call Reports";
  const { profileData } = useProfileData();

  const permissionType = JSON.parse(localStorage.getItem("permission"));
  const loginType = JSON.parse(localStorage.getItem("logintype"));
  const BooleanWithSingleEmployeeLogin =
    loginType === "employee" && permissionType.length === 0;

  const getFormattedDateTime = (date) => {
    if (!date || date === "-" || !moment(date).isValid()) {
      return {
        date: "-",
        time: "-",
      };
    }
    return {
      date: moment(date).format("DD MMM YYYY"),
      time: moment(date).format("hh:mm A"),
    };
  };

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [connectedCallData, setConnectedCallData] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFirstEffectRun, setIsFirstEffectRun] = useState(false);

  const column = [
    {
      name: <span className="font-weight-bold fs-13">SN.</span>,
      width: "5%",
      selector: (row, index) => index + 1,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Employee</span>,
      selector: (row) => row?.employeeName,
      cell: (row) => (
        <div className="mt-2 mb-2">
          <h5
            className="font-weight-bold fs-13"
            style={{ textTransform: "capitalize" }}
          >
            {row?.employeeName}
          </h5>
          <h6 className="text-center- font-weight-bold fs-13">
            {row?.countryCode + `-` + row?.employeePhone}
          </h6>
        </div>
      ),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13"> Team Name</span>,
      selector: (row) => row.team,
    },
    {
      name: <span className="font-weight-bold fs-13">Client Name</span>,
      selector: (row) => row?.clientName,
      cell: (row) => (
        <div className="mt-2 mb-2">
          <h5
            className="font-weight-bold fs-13"
            style={{ textTransform: "capitalize" }}
          >
            {row?.clientName || "Unknown"}
          </h5>
          <h6 className="text-center- font-weight-bold fs-13">
            {row?.clientPhone}
          </h6>
        </div>
      ),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Call Date</span>,
      selector: (row) => {
        const date = row?.callDateTime;
        return date && date !== "-" && moment(date).isValid()
          ? new Date(date).getTime()
          : 0;
      },
      cell: (row) => {
        const { date, time } = getFormattedDateTime(row?.callDateTime);
        return (
          <div className="mt-2 mb-2">
            <h5 className="font-weight-bold fs-13">{date}</h5>
            <h6 className="text-center font-weight-bold fs-13">{time}</h6>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Call Recording</span>,
      width: "37%",
      selector: (row) => (
        <>
          {row?.callRecording ? (
            <div
              style={{
                display: "inline-grid",
                marginBottom: "20px",
                marginTop: "10px",
              }}
            >
              <span style={{ height: "10px" }}>{formatTime(row?.duration)}</span>
              <audio
                style={{ height: "40px", marginTop: "15px" }}
                src={row?.callRecording}
                controls
              />
            </div>
          ) : (
            <div
              style={{
                display: "inline-grid",
                marginBottom: "20px",
                marginTop: "10px",
              }}
            >
             <>
                  <span style={{ height: "10px" }}>{ formatTime (row?.duration)}</span>
                  <span style={{ marginTop: "10px", color: "warning" }}>
                    Recording sync in progress...
                  </span>
                </>
            </div>
          )}
        </>
      ),
    }
    ,
    {
      name: <span className="font-weight-bold fs-13">Call Type</span>,
      selector: (row) => row?.callType,
      cell: (row) => {
        switch (row.callType) {
          case "OUTGOING":
            return (
              <>
                <div style={{ textTransform: "capitalize" }}>
                  {" "}
                  <i className="mdi mdi-phone-outgoing sn-out-going-text" />{" "}
                  <span className="badge badge-soft-info sn-out-going-call">
                    {" "}
                    {row.callType}
                  </span>
                </div>
              </>
            );
          case "REJECTED":
            return (
              <>
                <i className="mdi mdi-phone-remove sn-out-rejected-text" />
                <span className="badge badge-soft-warning sn-out-rejected-call">
                  {" "}
                  {row.callType}{" "}
                </span>
              </>
            );
          case "MISSED":
            return (
              <>
                <i className="mdi mdi-phone-missed sn-out-missed-text" />
                <span className="badge badge-soft-danger sn-out-missed-call">
                  {" "}
                  {row.callType}{" "}
                </span>
              </>
            );
          case "INCOMING":
            return (
              <>
                <i className="mdi mdi-phone-incoming text-success" />
                <span className="badge badge-soft-success sn-out-incoming-call">
                  {" "}
                  {row.callType}{" "}
                </span>
              </>
            );
          default:
            return (
              <span className="badge badge-soft-success"> {row.callType} </span>
            );
        }
      },
      sortable: true,
    },
  ];

  // Handle search with debouncing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [query]);

  // Fetch data when search query changes
  useEffect(() => {
    const fetchData = async () => {
      if (debouncedQuery.length >= 3 || debouncedQuery.length === 0) {
        try {
          const employeeIds =appliedFilters?.employees.length ?
            appliedFilters?.employees?.map((emp) => emp.id) :
            selectedEmployeeOptions2?.map((emp) => emp.id) ||
            [];

          if (BooleanWithSingleEmployeeLogin) {
            await fetchConnectedCallData(currentPage, perPage, debouncedQuery, [
              profileData._id,
            ]);
          } else {
            await fetchConnectedCallData(
              currentPage,
              perPage,
              debouncedQuery,
              employeeIds
            );
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };

    if (isFirstEffectRun) {
      fetchData();
    }
  }, [debouncedQuery, currentPage, perPage, appliedFilters]);

  // Initial data fetch
  useEffect(() => {
    if (!isFirstEffectRun) {
      const employeeIds = appliedFilters?.employees.length ?appliedFilters?.employees?.map((emp) => emp.id):
        selectedEmployeeOptions2?.map((option) => option?.id) || [];

      if (BooleanWithSingleEmployeeLogin) {
        fetchConnectedCallData(currentPage, perPage, "", [profileData._id]);
      } else if (employeeIds.length > 0) {
        fetchConnectedCallData(currentPage, perPage, "", employeeIds);
      }
      setIsFirstEffectRun(true);
    }
  }, [selectedEmployeeOptions, appliedFilters, profileData]);

  const fetchConnectedCallData = async (
    page,
    size = perPage,
    searchTerm = "",
    employeeIds
  ) => {
    setIsLoading(true);
    try {
      const data = {
        startDate: appliedFilters
          ? formatDate(appliedFilters.fromDate)
          : formatDate(selectedFromDate),
        endDate: appliedFilters
          ? formatDate(appliedFilters.toDate)
          : formatDate(selectedToDate),
        employeeIds: employeeIds,
      };

      const response = await api.create(
        `${CONNECTED_CALL}?page=${page}&limit=${size}&query=${searchTerm}`,
        data
      );

      if (response?.success) {
        const apiResponse = response.data;
        const tableData = (apiResponse?.contacts || []).map((item) => ({
          employeeName: item?.employeeInfo?.fullName || "Unknown",
          countryCode: item?.employeeInfo?.countryCode || "-",
          employeePhone: item?.employeeInfo?.phoneNumber || "-",
          clientName: item?.clientInfo?.name || "Unknown",
          clientPhone: item?.clientInfo?.mobileNumber || "-",
          duration : item?.callDetails?.duration || 0,
          callRecording: item?.callDetails?.recordingUrl,
          callDateTime: item?.callDetails?.dateTime || "-",
          callType: item?.clientInfo?.type || "-",
          team:item?.employeeInfo?.team[0]|| 'No Team'
        }));

        setConnectedCallData(tableData);
        setTotalRows(apiResponse?.totalConnectedCall || 0);
      }
    } catch (error) {
      HandleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  return (
    <React.Fragment>
      <div className="connected-call-page">
        <Container fluid>
          <Card className="h-100">
            <CardBody>
              <Row className="sn-team-managment-tble sn-user-tble-manin-manage">
                <Col lg={12}>
                  <div className="search-datatable-wrapper">
                    <CommanDataTable
                      setCurrentPage={setCurrentPage}
                      perPage={perPage}
                      handleSearch={handleSearch}
                      searchText={query}
                      column={column}
                      tableData={connectedCallData}
                      handlePerRowsChange={handlePerRowsChange}
                      totalRows={totalRows}
                      currentPage={currentPage}
                      isLoading={isLoading}
                      handlePageChange={handlePageChange}
                      noData="There are no records to display"
                      inputClass={
                        connectedCallData?.length > 0 || query
                          ? "d-block mb-4"
                          : "d-none"
                      }
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ConnectedConsolidate;
